import { channel } from '@redux-saga/core';
import { all, call, select, takeEvery } from '@redux-saga/core/effects';
import { AnalyticsTarget } from 'enums/AnalyticsTarget';
import { MessageFileType } from 'enums/MessageFileType';
import { DefaultRootState } from 'react-redux';
import { put } from 'redux-saga/effects';
import { services } from 'services/container';
import { createReachGoalActionBy } from 'utils/analytics';
import { downscaleImage, fileSizeStr } from 'utils/files';

import {
    FileActionType,
    fileNotUploaded,
    FileNotUploadedAction,
    fileUploaded,
    fileUploadProgress,
    FileUploadProgressAction,
    setFileUploadSize,
    UploadFileAction,
} from './files.actions';

const fileUploadChannel = channel();

function* uploadFileSaga() {
    yield takeEvery(FileActionType.UploadFile, function* (action: UploadFileAction) {
        const { uuid, file } = action.payload;
        const fileName = file.name;
        const nameParts = fileName.split('.');
        const ext = nameParts.at(-1).toLowerCase();

        let blob = file as Blob;

        if (blob.type.startsWith('image/')) {
            try {
                blob = yield call(downscaleImage, blob);
                if (file !== blob) {
                    yield put(setFileUploadSize(uuid, blob.size));
                }
            } catch (err) {
                services.log.error('Failed to downscale an image: ' + err);
            }
        }

        let sizeLimit: number = yield select(
            (state: DefaultRootState) => state.connection.channelInfo?.max_file_size,
        );

        if (!sizeLimit) {
            sizeLimit = 25 * 2 ** 20; // 25 meg
        }

        const allowedExtensions: string[] = yield select(
            (state: DefaultRootState) => state.files.allowedExtensions,
        );

        if (nameParts.length < 2 || !allowedExtensions.includes(ext)) {
            yield put(fileNotUploaded(uuid, `Недопустимое расширение файла`, true));
            return;
        }

        if (blob.size > sizeLimit) {
            yield put(
                fileNotUploaded(uuid, `Максимальный размер файла ${fileSizeStr(sizeLimit)}`, true),
            );
            return;
        }

        try {
            const response = yield call(() =>
                services.vo.uploadFile(blob, fileName, uuid, (percent) => {
                    fileUploadChannel.put(fileUploadProgress(uuid, percent));
                }),
            );

            const mediaType = blob.type.startsWith('image')
                ? MessageFileType.Image
                : MessageFileType.File;

            const uploadedFileInfo = response.data.files[0];
            if (uploadedFileInfo) {
                yield put(fileUploaded(uuid, mediaType, uploadedFileInfo));
            } else {
                yield put(fileNotUploaded(uuid));
            }
        } catch (err) {
            yield put(fileNotUploaded(uuid));
        }
    });
}

function* fileUploadProgressSaga() {
    yield takeEvery(fileUploadChannel, function* (action: FileUploadProgressAction) {
        yield put(action);
    });
}

function* fileNotUploadedSaga() {
    yield takeEvery(FileActionType.FileNotUploaded, function* (action: FileNotUploadedAction) {
        const { reason } = action.payload;
        services.analytics.sendAnalyticsAction(
            'Отображение ошибки под файлом',
            'Web-виджет. Ошибка загрузки файла' + reason ? ` (${reason})` : '',
        );
        services.analytics.send(createReachGoalActionBy(AnalyticsTarget.SHOW_FILE_NOT_UPLOADED));
    });
}

export function* filesSaga() {
    return yield all([uploadFileSaga(), fileUploadProgressSaga(), fileNotUploadedSaga()]);
}
